import { ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { ThemeToggle } from "./ThemeToggle";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  Mail,
  QrCode,
  Settings,
  Book,
  History,
  LogOut,
  Trophy,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import type { Business } from "@shared/schema";

interface DashboardLayoutProps {
  children: ReactNode;
}

const navItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Leaderboard", url: "/dashboard/leaderboard", icon: Trophy },
  { title: "Send Request", url: "/dashboard/send", icon: Mail },
  { title: "History", url: "/dashboard/history", icon: History },
  { title: "Staff", url: "/dashboard/staff", icon: Users },
  { title: "QR Codes", url: "/dashboard/qr-codes", icon: QrCode },
  { title: "Settings", url: "/dashboard/settings", icon: Settings },
  { title: "Docs", url: "/docs", icon: Book },
];

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [location, setLocation] = useLocation();
  const { signOut } = useAuth();

  const { data: business } = useQuery<Business>({
    queryKey: ["/api/business"],
  });

  const handleLogout = async () => {
    await signOut();
    setLocation("/");
  };

  const businessName = business?.name || "My Business";
  const planType = business?.planType || "free";
  const ownerInitials = business?.ownerEmail
    ? business.ownerEmail.split("@")[0].slice(0, 2).toUpperCase()
    : "??";

  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3.5rem",
  };

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <Sidebar>
          <SidebarHeader className="p-4 border-b border-sidebar-border">
            <div className="flex flex-col items-center gap-2">
              <img 
                src="/logo.png" 
                alt="ReviewKit" 
                className="h-10 object-contain"
              />
              <p className="text-sm font-medium text-center w-full truncate">
                {businessName}
              </p>
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navItems.map((item) => {
                    const isActive = location === item.url;
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <Link href={item.url}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter className="p-4 border-t border-sidebar-border">
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary/10 text-primary text-xs">
                  {ownerInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{business?.ownerEmail?.split("@")[0] || "Owner"}</p>
                <Badge variant="secondary" className="text-xs mt-0.5">
                  {planType}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                title="Log out"
                data-testid="button-logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </SidebarFooter>
        </Sidebar>
        <div className="flex flex-col flex-1 overflow-hidden">
          <header className="flex items-center justify-between gap-4 p-3 border-b">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <ThemeToggle />
          </header>
          <main className="flex-1 overflow-auto">{children}</main>
        </div>
      </div>
    </SidebarProvider>
  );
}
